import React, { useEffect } from 'react';
import { hot } from 'react-hot-loader';
import { datadogRum } from '@datadog/browser-rum';
import {
	useAuthenticationRequired,
	useMemberExperienceAuth,
} from '@guildeducationinc/auth-frontend-tools';
import { Chat } from 'src/components/Chat';
import { FooterContainer } from '@src/containers/FooterContainer';
import { LoadingOverlay } from 'src/components/LoadingOverlay';
import { Navigation } from 'src/components/Navigation';
import { Providers } from 'src/components/Providers';
import { Routes } from 'src/components/Routes';
import { SEO } from 'src/components/SEO/SEO';
import { ToastContainer } from 'src/components/Toast';
import { Banner } from './components/Banner';
import { GUILD_DOMAIN } from './utilities/envConfig';
import 'src/app.scss';

interface ApptentiveSDKPerson {
	unique_token: string;
	name?: string;
	email?: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	custom_data?: Record<string, any>;
}

interface ApptentiveSDK {
	createConversation: (config: { person: ApptentiveSDKPerson }) => void;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	engage: (eventName: string, eventData?: Record<string, any>) => void;
}

declare global {
	interface Window {
		ApptentiveSDK: ApptentiveSDK;
	}
}

const App: React.FC = () => {
	const context = useMemberExperienceAuth();
	useAuthenticationRequired({ context });
	const { authError, isAuthenticated, isLoading, userFromIdToken: user } = context;

	useEffect(() => {
		if (!isLoading && authError) {
			datadogRum.addAction('Error from useMemberExperienceAuthAndLogin hook', {
				catalogError: authError,
			});
		}
	}, [authError, isLoading]);

	useEffect(() => {
		if (user) {
			// Apptentive is used for Alchemer surveys
			window.ApptentiveSDK.createConversation({
				person: {
					unique_token: user.id,
					custom_data: {
						employer_id: user.employer_uuid,
						user_id: user.id,
					},
				},
			});

			// aa refers to the algolia analytics script
			window.aa('setUserToken', user.id);

			datadogRum.setUser({
				email: user.email,
				employer: user.employer,
				employerId: user.employer_uuid,
				id: user.id,
				roles: user.roles,
			});

			if (GUILD_DOMAIN === 'guildeducation.com') {
				datadogRum.startSessionReplayRecording();
			}
		}
	}, [user]);

	if (!isAuthenticated || isLoading || !user) {
		return <LoadingOverlay />;
	}

	return (
		<Providers>
			<Navigation />
			<Banner />
			<main className='background' id='main'>
				<SEO />
				<Routes />
				<ToastContainer autoClose={3000} />
				<Chat />
				<FooterContainer />
			</main>
		</Providers>
	);
};

export default hot(module)(App);
